import { mapState, mapGetters, mapActions } from 'vuex'
import CONSTANTS from '@/constants'
import { isSubscriptionPlan, isCertificationBundle } from '@/helpers/productClassification'

import { mixpanelEvent } from './mixpanelEvent'

const {
  PLANS,
  VOUCHERS,
  LIVE_VIRTUAL_TRAININGS,
  BLACK_FRIDAY_PRODUCT_BUNDLES,
  LEAP_YEAR_PROMO_PRODUCT_BUNDLES,
  SUMMER_SAVINGS_BUNDLE,
  PREMIUM_PLANS,
  TCS_PROMO_PRODUCT_BUNDLES,
} = CONSTANTS
const { INE_BLACK_FRIDAY_BUNDLE_ONE } = BLACK_FRIDAY_PRODUCT_BUNDLES
const { INE_SUMMER_SAVINGS_BUNDLE } = SUMMER_SAVINGS_BUNDLE
const { ANNUAL, MONTHLY } = PLANS
const BUNDLE_MERCHANT_IDS = [
  ...Object.values(BLACK_FRIDAY_PRODUCT_BUNDLES),
  ...Object.values(LEAP_YEAR_PROMO_PRODUCT_BUNDLES),
  ...Object.values(SUMMER_SAVINGS_BUNDLE),
  ...Object.values(TCS_PROMO_PRODUCT_BUNDLES),
].filter(value => typeof value === 'string')

export const updateCart = {
  mixins: [mixpanelEvent],
  computed: {
    ...mapState({
      fullState: state => state,
      availableLiveVirtualTrainings: state => state.products.availableLiveVirtualTrainings,
    }),
    ...mapGetters({
      selectedPlan: 'shoppingCart/selectedPlan',
      selectedExtension: 'shoppingCart/selectedExtension',
      selectedBundle: 'shoppingCart/selectedBundle',
      shoppingCart: 'shoppingCart/getShoppingCart',
      plans: 'products/plansByTerm',
      availablePlans: 'products/availablePlans',
      couponStr: 'coupons/getCoupon',
    }),
    skillDivePlan() {
      return this.plans.yearly.find(plan => plan.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL)
    },
  },
  methods: {
    ...mapActions({
      addProductToShoppingCart: 'shoppingCart/addProductToShoppingCart',
      removeProductFromShoppingCart: 'shoppingCart/removeProductFromShoppingCart',
      updateCouponObjStack: 'coupons/updateCouponObjStack',
      removeCouponAction: 'coupons/removeCoupon',
      priceDetailsUpdate: 'priceDetails/priceDetailsUpdate',
      createRecurlyInstance: 'recurly/createRecurlyInstance',
      calculateRecurlyPricing: 'recurly/calculateRecurlyPricing',
      setRecurlyCoupon: 'recurly/setRecurlyCoupon',
      getVoucherAvailabilityAction: 'shoppingCart/getVoucherAvailability',
    }),
    updateSelectedPlan(plan) {
      const duplicate = this.shoppingCart.filter(item => item.merchant_id === plan.merchant_id)
      const exists = this.shoppingCart.filter(item => isSubscriptionPlan(item))
      const planId = plan.merchant_id || plan.slug || ''
      // Don't remove Skill Dive when adding cert+prep bundles (API-flagged).
      const isCertPrepBundle = isCertificationBundle(plan)
      const bundleItemsOnCart = this.shoppingCart.filter(item => {
        const itemId = item.slug || item.merchant_id
        return BUNDLE_MERCHANT_IDS.includes(itemId)
      })
      const foundLVTonCart = this.shoppingCart.filter(
        item => item.merchant_id && Object.values(LIVE_VIRTUAL_TRAININGS).includes(item.merchant_id)
      )

      const existingSummerSavingBundle = this.shoppingCart.find(
        item => item.slug === INE_SUMMER_SAVINGS_BUNDLE
      )

      if (
        existingSummerSavingBundle &&
        (Object.values(PREMIUM_PLANS).includes(plan.merchant_id) ||
          Object.values(LIVE_VIRTUAL_TRAININGS).includes(plan.merchant_id))
      ) {
        this.removeProductFromCart(existingSummerSavingBundle)
      }

      bundleItemsOnCart
        .filter(item => {
          const itemId = item.slug || item.merchant_id
          const keepBundleForSkillDive =
            plan.merchant_id === PLANS.SKILL_DIVE_ANNUAL &&
            [
              BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_TWO,
              BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_THREE,
            ].includes(itemId)
          return itemId !== planId && !keepBundleForSkillDive
        })
        .forEach(item => this.removeProductFromCart(item))

      // remove skill dive from cart if plan is PremiumPlus or bundle is INE_BLACK_FRIDAY_BUNDLE_ONE
      const isBundleOne = plan.slug === INE_BLACK_FRIDAY_BUNDLE_ONE
      const isPremiumPlus = plan.merchant_id === PLANS.PREMIUM_PLUS

      if (isPremiumPlus || isBundleOne) {
        const skillDive = this.shoppingCart.find(
          item => item.merchant_id && item.merchant_id === PLANS.SKILL_DIVE_ANNUAL
        )
        if (skillDive) {
          this.skillDivePlan?.addOns.map(addOn =>
            this.removeAddOnFromPlan(this.skillDivePlan, addOn)
          )
          this.removeProductFromCart(skillDive)
        }
        this.shoppingCart.forEach(item => {
          if (item.merchant_id === PLANS.SKILL_DIVE_ANNUAL) {
            this.removeProductFromCart(item)
          }
        })
      }

      // remove LIVE_VIRTUAL_TRAININGS if a subscription is included with the LVT
      // subscription that is NOT SKILL_DIVE_ANNUAL
      if (plan.merchant_id !== PLANS.SKILL_DIVE_ANNUAL) {
        if (foundLVTonCart.length > 0 && foundLVTonCart.find(lvt => !!lvt.includedFeature)) {
          this.shoppingCart.forEach(item => {
            if (
              Object.values(LIVE_VIRTUAL_TRAININGS).includes(item.merchant_id) &&
              !!item.includedFeature
            ) {
              this.removeProductFromCart(item)
            }
          })
        }
      }
      if (plan.merchant_id === PLANS.SKILL_DIVE_ANNUAL) {
        const bundleOne = this.shoppingCart.find(item => item.slug === INE_BLACK_FRIDAY_BUNDLE_ONE)
        if (bundleOne) {
          this.removeProductFromCart(bundleOne)
        }
      }

      // remove [VOUCHERS.ICCA, VOUCHERS.EJPT]
      // if cart Has PLANS.ANNUAL Subscription
      if (plan.merchant_id === PLANS.ANNUAL) {
        this.shoppingCart.forEach(item => {
          if ([VOUCHERS.ICCA, VOUCHERS.EJPT].includes(item.merchant_id)) {
            this.removeProductFromCart(item)
          }
        })
      }
      const shouldKeepExistingPlan = item =>
        isCertPrepBundle && item.merchant_id === PLANS.SKILL_DIVE_ANNUAL
      const plansToRemove = exists.filter(item => item && !shouldKeepExistingPlan(item))

      if (exists.length && !duplicate.length) {
        if (plan.merchant_id !== PLANS.SKILL_DIVE_ANNUAL) {
          plansToRemove.forEach(item => this.removeProductFromCart(item))
        }
        this.addProductToCart(plan)
      } else if (exists.length || duplicate.length) {
        if (plan.merchant_id !== PLANS.SKILL_DIVE_ANNUAL) {
          plansToRemove.forEach(item => this.removeProductFromCart(item))
          duplicate.forEach(item => item && this.removeProductFromCart(item))
        }
        if (plan.merchant_id === PLANS.SKILL_DIVE_ANNUAL) {
          // PLANS.SKILL_DIVE_ANNUAL item here, is on cart and updated
          // but GA object needs to be updated
          this.gaAddToCartEvent(plan, Object.assign({}, this.$store, { rootState: this.fullState }))
        }
      } else {
        this.addProductToCart(plan)
      }
    },
    removeAddOnFromPlan(plan, item) {
      plan.addOnsOnCart = plan.addOnsOnCart.filter(addOn => addOn.id !== item.id)
    },
    updateSelectedBundle(bundle) {
      if (
        bundle.slug === BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_THREE ||
        bundle.merchant_id === PLANS.ANNUAL
      ) {
        const fundamentalsAnnual = this.availablePlans.find(
          item => item.merchant_id && item.merchant_id === PLANS.ANNUAL
        )
        this.shoppingCart.forEach(item => {
          if (
            [
              BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_ONE,
              BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_TWO,
              LEAP_YEAR_PROMO_PRODUCT_BUNDLES.INE_LEAP_YEAR_BUNDLE,
            ].includes(item.slug)
          )
            this.removeProductFromCart(item)
        })
        this.updateSelectedPlan(fundamentalsAnnual)
      } else {
        if (bundle.slug === INE_BLACK_FRIDAY_BUNDLE_ONE) {
          const skillDive = this.shoppingCart.find(
            item => item.merchant_id && item.merchant_id === PLANS.SKILL_DIVE_ANNUAL
          )
          if (skillDive) {
            this.skillDivePlan?.addOns.map(addOn =>
              this.removeAddOnFromPlan(this.skillDivePlan, addOn)
            )
            this.removeProductFromCart(skillDive)
          }
        }
        const duplicate = this.shoppingCart.filter(item => item.slug === bundle.slug)
        const exists = this.shoppingCart.filter(
          item =>
            item.slug &&
            [
              ...Object.values(BLACK_FRIDAY_PRODUCT_BUNDLES),
              ...Object.values(LEAP_YEAR_PROMO_PRODUCT_BUNDLES),
            ].includes(item.slug)
        )
        const isStandAloneOffer = [
          BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_ONE,
          BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_TWO,
          LEAP_YEAR_PROMO_PRODUCT_BUNDLES.INE_LEAP_YEAR_BUNDLE,
        ].includes(bundle.slug)

        if ((exists.length && !duplicate.length) || isStandAloneOffer) {
          exists.forEach(item => this.removeProductFromCart(item))
          this.shoppingCart.forEach(item => this.removeProductFromCart(item))
          this.addProductToCart(bundle)
        } else if (exists.length || duplicate.length) {
          exists.forEach(item => item && this.removeProductFromCart(item))
          duplicate.forEach(item => item && this.removeProductFromCart(item))
        } else {
          this.addProductToCart(bundle)
        }
      }
    },
    async addProductToCart(item) {
      await this.addProductToShoppingCart(item)
      await this.updateCouponObjStack()
      await this.priceDetailsUpdate()
      this.calculateRecurlyPricing(item)
    },
    async removeProductFromCart(item) {
      await this.removeProductFromShoppingCart(item)
      await this.priceDetailsUpdate()
    },
    clearCart() {
      const appliedCouponCode = this.couponStr
      this.shoppingCart.forEach(item => {
        if (item.merchant_id === CONSTANTS.PLANS.SKILL_DIVE_ANNUAL) {
          this.clearSkillDiveItemsFromCart(item)
        }
        this.removeProductFromCart(item)
      })
      this.removeCouponAction(appliedCouponCode)
    },
    updateSelectedLVT(lvt, isAddOn = false) {
      const exists = this.shoppingCart.find(item => item.id === lvt.id)
      const addOnValidation = () => {
        if (isAddOn) {
          lvt.addOn = true
          lvt.addOnChecked = true
        } else {
          lvt.addOn = false
          lvt.addOnChecked = false
        }
      }
      if (exists) {
        if (isAddOn) {
          lvt.addOn = false
          lvt.addOnChecked = false
        }
        this.removeProductFromCart(lvt)
      } else if (!lvt.user_receives_discount && lvt.includedFeature) {
        addOnValidation()
        this.updateSelectedPlan(lvt)
      } else {
        addOnValidation()
        this.addProductToCart(lvt)
      }
    },
    async updateSelectedCertification(certification) {
      const voucherAvailability = await this.getVoucherAvailabilityAction(certification)
      if (voucherAvailability) {
        const exists = this.shoppingCart.find(item => item.id === certification.id)
        if (exists) {
          this.removeProductFromCart(certification)
        } else {
          this.addProductToCart(certification)
        }
      }
    },
    clearSkillDiveItemsFromCart(skillDiveItem) {
      skillDiveItem.addOnsOnCart = []
      skillDiveItem.selectedAsAddOns = false
    },
  },
}
